/**
 * Profile picture control — uploaded picture always wins over the Discord
 * avatar, and shows up in chat and on the boards immediately.
 */
import { useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

export function AvatarUploader({ avatar, initials, username }) {
  const { refreshProfile, user } = useAuth();
  const toast = useToast();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const hasCustom = Boolean(user?.custom_avatar_url);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Images must be 4 MB or smaller.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/profile/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshProfile();
      toast.success("Profile picture updated.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/profile/avatar");
      await refreshProfile();
      toast.success("Back to your Discord picture.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4 mb-4">
      <div className="relative group">
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[var(--teal-soft)] flex items-center justify-center bg-black/40 text-xl glow-teal">
          {avatar ? (
            <img src={avatar} alt={username} className="w-full h-full object-cover" />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          title="Upload a profile picture"
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border border-[var(--teal-soft)] bg-black/80 text-[var(--teal)] opacity-80 hover:opacity-100 transition-opacity disabled:opacity-40"
          data-testid="avatar-upload-button"
        >
          <Camera size={13} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={pick}
          data-testid="avatar-file-input"
        />
      </div>
      <div>
        <div className="text-lg" data-testid="profile-username">{username}</div>
        <div className="text-xs text-[var(--muted)]">
          {busy ? "Saving…" : "PNG, JPEG, WEBP or GIF · up to 4 MB"}
        </div>
        {hasCustom && (
          <button
            onClick={remove}
            disabled={busy}
            className="text-xs text-[var(--muted)] hover:text-[var(--error)] mt-1.5 inline-flex items-center gap-1 transition-colors"
            data-testid="avatar-remove-button"
          >
            <Trash2 size={11} /> Use my Discord picture
          </button>
        )}
      </div>
    </div>
  );
}
